import { useState, useEffect, useCallback, useRef } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { genericWorkflowsClientApiRef } from '../api';
import type { WorkflowRun } from '../types';
import { useSelectedNamespace } from '../context';

interface UseWorkflowRunsResult {
  runs: WorkflowRun[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const POLLING_INTERVAL = 5000; // 5 seconds

/**
 * Hook to fetch workflow runs.
 * Automatically polls for updates when there are active runs.
 * Must be used within a NamespaceProvider.
 *
 * @param workflowName - Optional workflow name to filter runs by
 * @param namespaceName - Optional explicit namespace override for cluster workflows; falls back to NamespaceContext
 */
export function useWorkflowRuns(
  workflowName?: string,
  namespaceName?: string,
): UseWorkflowRunsResult {
  const client = useApi(genericWorkflowsClientApiRef);
  const contextNamespace = useSelectedNamespace();
  const resolvedNamespace = namespaceName ?? contextNamespace;

  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const fetchRuns = useCallback(async () => {
    if (!resolvedNamespace) {
      setRuns([]);
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const response = await client.listWorkflowRuns(
        resolvedNamespace,
        workflowName,
      );
      setRuns(response.items);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [client, resolvedNamespace, workflowName]);

  // Check if there are any active runs (Pending or Running)
  const hasActiveRuns = runs.some(run => {
    const status = (run.phase || run.status)?.toLowerCase();
    return status === 'pending' || status === 'running';
  });

  // Set up polling when there are active runs
  useEffect(() => {
    if (hasActiveRuns) {
      pollingRef.current = setInterval(() => {
        fetchRuns();
      }, POLLING_INTERVAL);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [hasActiveRuns, fetchRuns]);

  // Initial fetch
  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  return {
    runs,
    loading,
    error,
    refetch: fetchRuns,
  };
}
